import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ResponderRule } from '@mailpilot/shared';
import clsx from 'clsx';
import { OctagonPause, Pencil, Plus, Trash2, Workflow } from 'lucide-react';
import { api, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useMailboxes } from '../../hooks/useMailboxes';
import { useSettings, useUpdateSettings } from '../../hooks/useMe';
import { AccountBadge, Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Skeleton, Switch } from '../../components/ui';
import { useConfirm, useToast } from '../../components/ui';
import { TipIconButton } from '../../components/settings/SettingsPrimitives';
import { ResponderRuleEditor } from '../../components/settings/ResponderRuleEditor';
import { ResponderDryRun } from '../../components/settings/ResponderDryRun';
import { MODE_META } from '../../components/settings/ResponderShared';
import { SettingsTitle } from './SettingsLayout';

type RulesRes = ResOf<'GET /api/rules'>;

export function ResponderSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const mailboxesQ = useMailboxes();
  const mailboxes = mailboxesQ.data?.mailboxes ?? [];
  const mbMap = useMemo(() => new Map(mailboxes.map((m) => [m.id, m])), [mailboxes]);
  const [editing, setEditing] = useState<ResponderRule | 'new' | null>(null);

  const rulesQ = useQuery({ queryKey: qk.rules, queryFn: () => api('GET /api/rules') });
  const rules = useMemo(() => [...(rulesQ.data?.rules ?? [])].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt)), [rulesQ.data]);

  const toggle = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) => api('PATCH /api/rules/:id', { params: { id: v.id }, body: { enabled: v.enabled } }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: qk.rules });
      const prev = qc.getQueryData<RulesRes>(qk.rules);
      qc.setQueryData<RulesRes>(qk.rules, (p) => (p ? { rules: p.rules.map((r) => (r.id === v.id ? { ...r, enabled: v.enabled } : r)) } : p));
      return { prev };
    },
    onError: (e, _v, c) => {
      if (c?.prev) qc.setQueryData(qk.rules, c.prev);
      toast.error(e);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: qk.rules }),
  });

  const del = useMutation({
    mutationFn: (id: string) => api('DELETE /api/rules/:id', { params: { id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.rules });
      toast.success('Rule deleted');
    },
    onError: (e) => toast.error(e),
  });

  const askDelete = async (r: ResponderRule) => {
    const ok = await confirm({ title: `Delete "${r.name}"?`, message: 'Messages that matched this rule will fall through to the next rule, or get suggestions only.', confirmLabel: 'Delete rule', danger: true });
    if (ok) del.mutate(r.id);
  };

  const paused = settings?.globalPause ?? false;
  const nextPriority = rules.length ? Math.max(...rules.map((r) => r.priority)) + 10 : 10;

  return (
    <div>
      <SettingsTitle
        title="Responders & rules"
        description="By default MailPilot only suggests replies for you to review. Rules let you turn on acknowledgements or carefully controlled automatic replies."
      />
      <div className="space-y-6">
        {/* Global pause */}
        <Card className={clsx(paused && '!border-danger/40 ring-4 ring-danger/10')}>
          <CardBody className="flex items-start justify-between gap-6 !py-5">
            <div className="flex min-w-0 items-start gap-3.5">
              <span className={clsx('mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] transition-colors', paused ? 'bg-danger-soft text-danger-ink' : 'bg-primary-soft text-primary-ink')}>
                <OctagonPause className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <label htmlFor="global-pause" className="block cursor-pointer text-[0.9375rem] font-semibold leading-snug text-ink">
                  {paused ? 'All automatic sending is paused' : 'Pause all automatic sending'}
                </label>
                <p className="mt-1 text-[0.8125rem] leading-relaxed text-muted">Stops every automatic send immediately (acknowledgements and auto replies). Suggestions still work.</p>
              </div>
            </div>
            <Switch id="global-pause" size="lg" checked={paused} disabled={!settings} onChange={(globalPause) => updateSettings.mutate({ globalPause })} />
          </CardBody>
        </Card>

        {/* Rules */}
        <Card>
          <CardHeader
            icon={<Workflow className="h-5 w-5" aria-hidden />}
            title="Rules"
            description="Evaluated in priority order (lowest number first). The first matching rule decides what happens."
            actions={
              rules.length > 0 && (
                <Button variant="primary" size="sm" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setEditing('new')}>
                  New rule
                </Button>
              )
            }
          />
          {rulesQ.isLoading ? (
            <CardBody className="space-y-3" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-8 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3.5 w-48" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-11 rounded-full" />
                </div>
              ))}
            </CardBody>
          ) : rulesQ.isError ? (
            <ErrorState error={rulesQ.error} onRetry={() => void rulesQ.refetch()} title="Couldn't load your rules" />
          ) : rules.length === 0 ? (
            <EmptyState
              icon={<Workflow className="h-6 w-6" aria-hidden />}
              title="No rules yet"
              description="Without rules MailPilot suggests replies for you to review and never sends anything on its own."
              actions={
                <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setEditing('new')}>
                  Create your first rule
                </Button>
              }
            />
          ) : (
            <>
              <CardDivider className="mt-5" />
              <ul className="divide-y divide-line/70">
                {rules.map((r) => {
                  const mb = r.mailboxId ? mbMap.get(r.mailboxId) : null;
                  const meta = MODE_META[r.mode];
                  return (
                    <li key={r.id} className={clsx('group flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-4 transition-colors hover:bg-raised/40', !r.enabled && 'opacity-60')}>
                      <span className="flex h-8 min-w-[2.5rem] shrink-0 items-center justify-center rounded-lg bg-sunken px-2 text-xs font-semibold tabular-nums text-muted" title="Priority — lower runs first">
                        #{r.priority}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-semibold text-ink">{r.name}</span>
                          <Badge tone={meta.tone}>{meta.short}</Badge>
                          {r.mode === 'auto_reply' && r.requireApproval && <Badge tone="neutral">Needs approval</Badge>}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted">
                          {mb ? <AccountBadge color={mb.color} label={mb.displayName || mb.email} /> : r.mailboxId ? <Badge>Unknown mailbox</Badge> : <span>All mailboxes</span>}
                        </div>
                      </div>
                      <div className="ml-auto flex items-center gap-2">
                        <Switch checked={r.enabled} onChange={(enabled) => toggle.mutate({ id: r.id, enabled })} id={`rule-enabled-${r.id}`} />
                        <label htmlFor={`rule-enabled-${r.id}`} className="sr-only">
                          Enable rule {r.name}
                        </label>
                        <div className="flex items-center transition-opacity [@media(hover:hover)]:opacity-50 group-hover:opacity-100 group-focus-within:opacity-100">
                          <TipIconButton size="sm" label={`Edit rule ${r.name}`} icon={<Pencil className="h-4 w-4" aria-hidden />} onClick={() => setEditing(r)} />
                          <TipIconButton size="sm" label={`Delete rule ${r.name}`} tipPlacement="bottom-end" icon={<Trash2 className="h-4 w-4" aria-hidden />} onClick={() => void askDelete(r)} className="hover:!bg-danger-soft hover:!text-danger-ink" />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </Card>

        <ResponderDryRun />
      </div>

      {editing && (
        <ResponderRuleEditor
          key={editing === 'new' ? 'new' : editing.id}
          open
          onClose={() => setEditing(null)}
          rule={editing === 'new' ? null : editing}
          mailboxes={mailboxes}
          timezone={settings?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC'}
          nextPriority={nextPriority}
        />
      )}
    </div>
  );
}
